import { Box, CircularProgress, Divider, Tab, Tabs } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDashboardOrder from '../../../hooks/dashboard/useDashboardOrder';
import { setSnackBar } from '../../../states/app/staticSlice.js';
import { FilterComponent } from './components/FilterComponent.jsx';
import { SearchComponent } from './components/SearchComponent.jsx';
import { TableComponent } from './components/TableComponent.jsx';
import { ORDER_CANCEL } from './config/OrderCancel';
import { ORDER_CANCELLABLE } from './config/OrderCancellable';
import { ORDER_RETRACTABLE } from './config/OrderRetractable.jsx';
import { ORDER_RETRACTED } from './config/OrderRetracted';
import { SAV_ORDER } from './config/SavOrder';

const CONTENT_TAB = {
  CANCELLED: ORDER_CANCEL,
  CANCELLABLE: ORDER_CANCELLABLE,
  RETRACTABLE: ORDER_RETRACTABLE,
  RETRACTED: ORDER_RETRACTED,
  SAV_ORDER: SAV_ORDER,
};

const DashboardOrder = () => {
  const profile = useSelector((state) => state.login.profile);
  const tabList = [
    CONTENT_TAB.CANCELLED,
    CONTENT_TAB.CANCELLABLE,
    CONTENT_TAB.RETRACTABLE,
    CONTENT_TAB.RETRACTED,
    CONTENT_TAB.SAV_ORDER,
  ];
  const [page, setPage] = useState(0);
  const [contentTab, setContentTab] = useState(tabList[0]);
  const [formData, setFormData] = useState({
    filters: contentTab.filters,
    searchFilter: null,
    orderBy: contentTab.sortOptions[0].value,
    orderByIndex: 0,
    limit: 10,
    offset: 0,
    query: contentTab.query,
  });

  const { data, res, isLoading, isValidating, mutate } =
    useDashboardOrder(formData);
  const [loading, setLoading] = useState(isLoading);
  const dispatch = useDispatch();

  const handleChange = (event, newValue) => {
    setContentTab(newValue);
  };

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPage(newPage);
    },
    [formData.limit]
  );

  useEffect(() => {
    setFormData((state) => ({
      ...state,
      limit: 10,
      offset: 0,
      filters: contentTab.filters,
      searchFilter: null,
      orderBy: contentTab.sortOptions[0].value,
      orderByIndex: 0,
      query: contentTab.query,
    }));
    setPage(0);
  }, [contentTab]);

  useEffect(() => {
    mutate();
    if (res && res?.status > 300) {
      const messageError = `Error: ${res?.status} - ${res?.statusText}: DashboardOrder`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } else {
      dispatch(setSnackBar({ open: false, message: null, color: null }));
    }
  }, [formData]);

  useEffect(() => {
    if (isLoading || isValidating) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoading, isValidating]);

  useEffect(() => {
    setFormData((state) => ({
      ...state,
      offset: page * formData.limit,
    }));
  }, [page]);

  return (
    <>
      <Box sx={{ padding: 3, fontWeight: 'bold', fontSize: 24 }}>
        Bonjour {profile.firstName},
      </Box>
      <Tabs value={contentTab} onChange={handleChange}>
        {tabList.map((tab) => (
          <Tab
            key={tab.contentTab}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {tab.label}
                {tab.label === contentTab.label ? (
                  loading ? (
                    <CircularProgress color="inherit" size={10} />
                  ) : (
                    ` (${data?.totalCount})`
                  )
                ) : null}
              </Box>
            }
            value={tab}
          />
        ))}
      </Tabs>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <SearchComponent
          formData={formData}
          setFormData={setFormData}
          loading={loading}
        />
        <Divider orientation="vertical" flexItem />
        <FilterComponent
          formData={formData}
          setFormData={setFormData}
          orderType={contentTab}
        />
      </Box>
      <TableComponent
        limit={formData.limit}
        data={data}
        orderType={contentTab}
        loading={loading}
        page={page}
        handleChangePage={handleChangePage}
      />
    </>
  );
};

export default DashboardOrder;
